import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import NavPlusIcon from "../icons/NavPlusIcon";
import NewButton from "../NewButton";
import { servicesMegaColumns, topNavLinks } from "../../data/headerNav";

/** Apple globalnav: cubic-bezier(0.4, 0, 0.6, 1) */
const appleEase = [0.4, 0, 0.6, 1];

const panelVariants = {
    hidden: { clipPath: "inset(0 0 100% 0)" },
    show: {
        clipPath: "inset(0 0 0% 0)",
        transition: { duration: 0.48, ease: appleEase },
    },
    exit: {
        clipPath: "inset(0 0 100% 0)",
        transition: { duration: 0.36, ease: appleEase },
    },
};

const listVariants = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.045, delayChildren: 0.12 },
    },
    exit: {
        transition: { staggerChildren: 0.025, staggerDirection: -1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: appleEase },
    },
    exit: {
        opacity: 0,
        y: -6,
        transition: { duration: 0.18, ease: appleEase },
    },
};

/** + sits mid-aligned with label */
const PlusMark = ({ open }) => (
    <div
        aria-hidden="true"
        style={{
            width: 14,
            height: 14,
            marginLeft: 8,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <div
            style={{
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: open ? "rotate(45deg)" : "rotate(0deg)",
                transition: "transform 0.32s ease",
            }}
        >
            <NavPlusIcon />
        </div>
    </div>
);

/** Dual-line reveal → #417B5A */
const NavLabel = ({ children }) => (
    <span className="tw:relative tw:inline-block tw:overflow-hidden tw:h-[1.15em] tw:leading-[1.15]">
        <span className="tw:block tw:transition-transform tw:duration-500 tw:ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/nav:-translate-y-[1.15em] group-active/nav:-translate-y-[1.15em]">
            <span className="tw:block tw:h-[1.15em]">{children}</span>
            <span className="tw:block tw:h-[1.15em] tw:text-[#417B5A]" aria-hidden="true">
                {children}
            </span>
        </span>
    </span>
);

const AccordionPanel = ({ open, children }) => (
    <AnimatePresence initial={false}>
        {open ? (
            <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.36, ease: appleEase }}
                className="tw:overflow-hidden"
            >
                <motion.div
                    initial="hidden"
                    animate="show"
                    variants={{
                        hidden: {},
                        show: { transition: { staggerChildren: 0.028, delayChildren: 0.04 } },
                    }}
                    className="tw:pb-5 tw:pt-1"
                >
                    {children}
                </motion.div>
            </motion.div>
        ) : null}
    </AnimatePresence>
);

const SubLink = ({ to, onClick, children }) => (
    <motion.div
        variants={{
            hidden: { opacity: 0, y: 6 },
            show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: appleEase } },
        }}
    >
        <Link
            to={to}
            onClick={onClick}
            className="tw:block tw:py-1.5 tw:text-[17px] tw:font-normal tw:text-[#1d1d1f] tw:no-underline tw:transition-colors tw:duration-[320ms] tw:ease-[cubic-bezier(0.4,0,0.6,1)] tw:hover:text-[#417B5A]"
        >
            {children}
        </Link>
    </motion.div>
);

const MenuRow = ({ children, onClick, as: Comp = "button", to, expanded, badge }) => {
    const className =
        "tw:group/nav tw:w-full tw:inline-flex tw:items-center tw:bg-transparent tw:border-0 tw:py-3.5 tw:px-0 tw:text-left tw:cursor-pointer tw:no-underline tw:text-[28px] tw:md:text-[32px] tw:font-medium tw:leading-[1.15]  tw:text-[#1d1d1f]";

    const content = (
        <>
            <NavLabel>{children}</NavLabel>
            {typeof expanded === "boolean" ? <PlusMark open={expanded} /> : null}
            {badge ? (
                <span className="tw:text-[15px] tw:font-bold tw:leading-none tw:ml-2 tw:text-[#417B5A] tw:-translate-y-3">
                    {badge}
                </span>
            ) : null}
        </>
    );

    if (Comp === "link") {
        return (
            <Link to={to} onClick={onClick} className={className}>
                {content}
            </Link>
        );
    }

    return (
        <button type="button" className={className} aria-expanded={expanded} onClick={onClick}>
            {content}
        </button>
    );
};

/**
 * Panel sits under the site header so logo + hamburger/X stay in the exact same place.
 */
export default function MobileNav({ open, onClose, servicesOpen, onToggleServices }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    key="mobile-nav"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Menu"
                    variants={panelVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    className="tw:lg:hidden tw:fixed tw:left-0 tw:right-0 tw:bottom-0 tw:top-[60px] tw:md:top-[75px] tw:z-[60] tw:flex tw:flex-col tw:bg-white tw:overflow-hidden"
                    style={{ willChange: "clip-path" }}
                    data-lenis-prevent
                >
                    <motion.nav
                        className="tw:relative tw:flex-1 tw:overflow-y-auto tw:overscroll-contain tw:px-5 tw:md:px-8 tw:pt-4 tw:pb-4"
                        aria-label="Mobile"
                        data-lenis-prevent
                        variants={listVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                    >
                        {topNavLinks.map((item) => {
                            const isServices = item.id === "services" && item.hasMenu;
                            const expanded = isServices ? servicesOpen : undefined;

                            return (
                                <motion.div key={item.id} variants={itemVariants}>
                                    <MenuRow
                                        as={isServices ? "button" : "link"}
                                        to={item.to}
                                        expanded={expanded}
                                        badge={item.badge}
                                        onClick={isServices ? onToggleServices : onClose}
                                    >
                                        {item.label}
                                    </MenuRow>

                                    {isServices ? (
                                        <AccordionPanel open={servicesOpen}>
                                            <div className="tw:pl-0 tw:flex tw:flex-col tw:gap-5">
                                                {servicesMegaColumns.map((column) => (
                                                    <div key={column.title}>
                                                        <motion.p
                                                            variants={{
                                                                hidden: { opacity: 0 },
                                                                show: {
                                                                    opacity: 1,
                                                                    transition: { duration: 0.28, ease: appleEase },
                                                                },
                                                            }}
                                                            className="tw:text-[12px] tw:font-normal tw:text-[#6e6e73] tw:mb-2 tw:m-0"
                                                        >
                                                            {column.title}
                                                        </motion.p>
                                                        <div className="tw:flex tw:flex-col">
                                                            {column.links.map((link) => (
                                                                <SubLink key={link.label} to={link.to} onClick={onClose}>
                                                                    {link.label}
                                                                </SubLink>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </AccordionPanel>
                                    ) : null}
                                </motion.div>
                            );
                        })}
                    </motion.nav>

                    <motion.div
                        className="tw:relative tw:shrink-0 tw:px-5 tw:md:px-8 tw:pt-3 tw:pb-5 tw:flex tw:flex-row tw:flex-wrap tw:items-center tw:gap-2.5"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.4, ease: appleEase, delay: 0.28 },
                        }}
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                    >
                        <NewButton
                            to="/contact"
                            onClick={onClose}
                            variant="primary"
                            className="tw:!bg-[#417B5A] tw:!text-white tw:!h-10 tw:!px-5 tw:!text-sm tw:w-auto tw:!shadow-none tw:shrink-0"
                        >
                            Start a Project
                        </NewButton>
                        <Link
                            to="/portfolio"
                            onClick={onClose}
                            className="tw:inline-flex tw:items-center tw:justify-center tw:h-10 tw:px-5 tw:rounded-full tw:bg-[#AA5042] tw:no-underline tw:shadow-none tw:shrink-0"
                            aria-label="Our portfolio"
                        >
                            <img
                                src="/assets/image/our-portfolio.svg"
                                alt="Our portfolio"
                                width={135}
                                height={43}
                                className="tw:w-[88px] tw:h-auto tw:max-w-none tw:block"
                            />
                        </Link>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}